import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, XCircle, Clock, ArrowRight, Home, FileText, BookOpen } from 'lucide-react';

const PagoResultadoPage = () => {
  const [searchParams] = useSearchParams();
  const tipo = searchParams.get('tipo');
  const status = searchParams.get('status') || window.location.pathname.split('/').pop();
  const paymentId = searchParams.get('payment_id');
  const externalReference = searchParams.get('external_reference');

  // Determinar el estado basado en la URL
  const getEstado = () => {
    if (window.location.pathname.includes('exito')) return 'success';
    if (window.location.pathname.includes('error')) return 'error';
    if (window.location.pathname.includes('pendiente')) return 'pending';
    return status;
  };

  const estado = getEstado();

  // Configuración por tipo de pago
  const configs = {
    suscripcion: {
      titulo: 'Suscripción',
      successMessage: '¡Tu suscripción ha sido activada correctamente!',
      successAction: { text: 'Ir al Panel', href: '/panel', icon: Home },
      pendingMessage: 'Tu pago está siendo procesado. Te notificaremos cuando esté confirmado.',
      errorMessage: 'No pudimos procesar tu pago de suscripción. Por favor, intenta nuevamente.'
    },
    usuarios: {
      titulo: 'Usuarios Adicionales',
      successMessage: '¡Los usuarios adicionales han sido agregados a tu cuenta!',
      successAction: { text: 'Administrar Usuarios', href: '/panel/usuarios', icon: Home },
      pendingMessage: 'Tu pago está siendo procesado. Los usuarios se agregarán cuando se confirme.',
      errorMessage: 'No pudimos procesar tu compra. Por favor, intenta nuevamente.'
    },
    curso: {
      titulo: 'Inscripción a Curso',
      successMessage: '¡Tu inscripción al curso ha sido confirmada!',
      successAction: { text: 'Ver Cursos', href: '/capacitaciones', icon: BookOpen },
      pendingMessage: 'Tu pago está siendo procesado. Recibirás un email de confirmación.',
      errorMessage: 'No pudimos procesar tu inscripción. Por favor, intenta nuevamente.'
    },
    documentos: {
      titulo: 'Compra de Documentos',
      successMessage: '¡Tus documentos están disponibles para consulta!',
      successAction: { text: 'Ver Documentos', href: '/panel/documentos', icon: FileText },
      pendingMessage: 'Tu pago está siendo procesado. Los documentos estarán disponibles pronto.',
      errorMessage: 'No pudimos procesar tu compra. Por favor, intenta nuevamente.'
    }
  };

  const config = configs[tipo] || configs.suscripcion;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8">
          {/* SUCCESS */}
          {estado === 'success' && (
            <div className="text-center space-y-6">
              <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">¡Pago Exitoso!</h2>
                <p className="text-gray-600">{config.successMessage}</p>
              </div>
              
              {paymentId && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <span className="text-gray-500">ID de transacción:</span>
                  <br />
                  <span className="font-mono text-gray-700">{paymentId}</span>
                </div>
              )}

              <div className="space-y-3">
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700">
                  <Link to={config.successAction.href}>
                    <config.successAction.icon className="h-4 w-4 mr-2" />
                    {config.successAction.text}
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">
                    Ir al Inicio
                  </Link>
                </Button>
              </div>
            </div>
          )}

          {/* PENDING */}
          {estado === 'pending' && (
            <div className="text-center space-y-6">
              <div className="bg-amber-100 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
                <Clock className="h-12 w-12 text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Pago Pendiente</h2>
                <p className="text-gray-600">{config.pendingMessage}</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                <h3 className="font-medium text-amber-800 mb-2">¿Qué significa esto?</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Tu pago está siendo verificado</li>
                  <li>• Puede tomar algunos minutos u horas</li>
                  <li>• Te enviaremos un email cuando se confirme</li>
                </ul>
              </div>

              <Button asChild className="w-full">
                <Link to="/">
                  Volver al Inicio
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          )}

          {/* ERROR */}
          {estado === 'error' && (
            <div className="text-center space-y-6">
              <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto flex items-center justify-center">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Pago No Procesado</h2>
                <p className="text-gray-600">{config.errorMessage}</p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                <h3 className="font-medium text-red-800 mb-2">Posibles causas:</h3>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Fondos insuficientes</li>
                  <li>• Tarjeta vencida o bloqueada</li>
                  <li>• Error temporal en el procesador</li>
                </ul>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={() => window.history.back()}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  Reintentar Pago
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">
                    Ir al Inicio
                  </Link>
                </Button>
              </div>

              <p className="text-xs text-gray-500">
                ¿Necesitas ayuda? Contáctanos a{' '}
                <a href="mailto:soporte@cmpublicaciones.com" className="text-teal-600 hover:underline">
                  soporte@cmpublicaciones.com
                </a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PagoResultadoPage;
